import React, { useState, useRef, useEffect } from 'react';
import { Share2, MessageCircle, Link as LinkIcon, X, Check } from 'lucide-react';
import { formatCOP } from '../mock';
import { track } from '../lib/analytics';

/**
 * ShareButton — share a property via WhatsApp, native share sheet, or copy-link.
 *
 * Props:
 *  - property: { id, title, price, city, operation }
 *  - variant: 'icon' (compact, for cards) | 'button' (full, for detail page)
 *  - className: extra classes for the trigger
 *  - testId: data-testid override
 */
export default function ShareButton({ property, variant = 'icon', className = '', testId }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const url = typeof window !== 'undefined' ? `${window.location.origin}/propiedad/${property.id}` : '';
  const opLabel = property.operation === 'arriendo' ? 'arriendo' : 'venta';
  const priceLine = `${formatCOP(property.price)}${property.operation === 'arriendo' ? ' /mes' : ''}`;
  const message =
    `🏠 ${property.title}\n` +
    `💰 ${priceLine} · ${opLabel}\n` +
    `📍 ${property.city}\n\n` +
    `Ver en habita.co:\n${url}`;

  const trackShare = (channel) => {
    try {
      track('share_property', {
        property_id: property.id,
        channel,
        city: property.city,
        operation: property.operation,
        price: property.price,
      });
    } catch {}
  };

  const handleClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Prefer native share on mobile (best UX, includes WhatsApp + IG + iMessage etc.)
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({
          title: property.title,
          text: `${property.title} — ${priceLine} · ${property.city}`,
          url,
        });
        trackShare('native');
        return;
      } catch (err) {
        // User cancelled or share API not supported in this context → fall through to popover
        if (err && err.name === 'AbortError') {
          trackShare('native_cancel');
          return;
        }
      }
    }

    // Desktop / browsers without navigator.share → open popover
    setOpen((o) => !o);
  };

  const shareWhatsapp = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    const link = `https://wa.me/?text=${encodeURIComponent(message)}`;
    trackShare('whatsapp');
    window.open(link, '_blank', 'noopener,noreferrer');
    setOpen(false);
  };

  const copyLink = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      trackShare('copy_link');
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Last resort: text fallback
      const ta = document.createElement('textarea');
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); setCopied(true); trackShare('copy_link'); } catch {}
      document.body.removeChild(ta);
      setTimeout(() => setCopied(false), 1800);
    }
  };

  const triggerCls =
    variant === 'button'
      ? `inline-flex items-center justify-center gap-2 h-11 px-4 rounded-full border border-gray-200 hover:border-habita text-gray-700 text-sm font-medium transition-colors ${className}`
      : `w-9 h-9 rounded-full bg-white/95 hover:bg-white flex items-center justify-center transition-colors shadow-sm ${className}`;

  return (
    <div className="relative inline-flex" ref={ref}>
      <button
        type="button"
        onClick={handleClick}
        className={triggerCls}
        data-testid={testId || `share-btn-${property.id}`}
        aria-label="Compartir propiedad"
      >
        <Share2 className={variant === 'button' ? 'w-4 h-4' : 'w-4 h-4 text-gray-700'} />
        {variant === 'button' && <span>Compartir</span>}
      </button>

      {open && (
        <div
          className="absolute top-full right-0 mt-2 w-64 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden z-30"
          onClick={(e) => e.stopPropagation()}
          data-testid="share-popover"
        >
          <div className="flex items-center justify-between px-4 pt-3 pb-1">
            <p className="text-xs font-bold uppercase tracking-widest text-gray-500">Compartir</p>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setOpen(false); }}
              className="w-6 h-6 rounded-full hover:bg-gray-100 flex items-center justify-center"
              aria-label="Cerrar"
            >
              <X className="w-3.5 h-3.5 text-gray-500" />
            </button>
          </div>
          <button
            type="button"
            onClick={shareWhatsapp}
            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-emerald-50 transition-colors text-left"
            data-testid="share-whatsapp"
          >
            <span className="w-9 h-9 rounded-full bg-emerald-500 text-white flex items-center justify-center shrink-0">
              <MessageCircle className="w-4 h-4 fill-current" />
            </span>
            <div>
              <p className="text-sm font-semibold text-habita-dark">WhatsApp</p>
              <p className="text-xs text-gray-500">Comparte directo</p>
            </div>
          </button>
          <button
            type="button"
            onClick={copyLink}
            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left border-t border-gray-100"
            data-testid="share-copy-link"
          >
            <span className="w-9 h-9 rounded-full bg-gray-100 text-gray-700 flex items-center justify-center shrink-0">
              {copied ? <Check className="w-4 h-4 text-habita" /> : <LinkIcon className="w-4 h-4" />}
            </span>
            <div>
              <p className="text-sm font-semibold text-habita-dark">{copied ? '¡Copiado!' : 'Copiar enlace'}</p>
              <p className="text-xs text-gray-500 truncate">{url.replace(/^https?:\/\//, '')}</p>
            </div>
          </button>
        </div>
      )}
    </div>
  );
}
